import * as React from "react";
import { IPivotItemProps, IPivotStyles, Pivot, PivotItem, registerIcons } from "@fluentui/react";
import { FormattedString } from "../FormattedString/FormattedString";
import {
  Form,
  FormFieldErrors,
  FormInputGroup,
  FormInputGroupLayout,
  FormInterface,
  FormStates,
  FormValues,
  ValidationState,
} from "../Input/Form";
import { CircleMultiplySolid } from "../Svg/icons";

export enum InputWizardTabNavigation {
  /**
   * User allowed to navigate between tabs in any order
   */
  FREE = "FREE",
  /**
   * User is only allowed to navigate between the tabs sequentially going forward but can jump to any tab going backward
   */
  SEQUENTIAL = "SEQUENTIAL",
}

export interface InputWizardComponent {
  /**
   * Method to retrieve the map for the field and it corresponding label
   */
  getFormFieldLabelMap: () => Map<string, string>;
  /**
   * Method to retrieve the map for the field and it corresponding group name
   */
  getFormFieldGroupMap: () => Map<string, string>;
  /**
   * Method to retrieve the form values
   */
  getFormValues: () => FormValues | undefined;
  /**
   * Method when validation is enabled on the InputWizardComponent
   */
  enableValidation: (group?: string, noNavigation?: boolean) => void;
  /**
   * Method to check if validation is enabled on the InputWizardComponent
   */
  isValidationEnabled: (group?: string) => boolean | undefined;
  /**
   * Method to check if there are fields with validation
   */
  hasValidation: (group?: string) => boolean | undefined;
  /**
   * Method for submit action on the InputWizardComponent
   */
  submit: () => void;
  /*
  * Method for next action on the InputWizardComponent
  */
  next: () => void;
  /**
   * Method for the previous action on the InputWizardComponent
   */
  previous: () => void;
}

export interface InputWizardItem {
  /**
   * Header for the InputWizardItem
   */
  header: string;
  /**
   * Group name for the InputWizardItem
   */
  groupName: string;
  /**
   * Layout for the InputWizardItem
   */
  layout?: FormInputGroupLayout;
  /**
   * Content to be displayed for the InputWizardItem
   */
  content: React.ReactNode;
  /**
   * Description for the InputWizardItem
   */
  description?: string;
  /**
   * Callback when the InputWizardItem is selected
   */
  onSelected?: (ndx: number) => void;
  /**
   * Callback when the InputWizardItem is unselected
   */
  onUnselecting?: (ndx: number) => void;
  /**
   * data-test-id HTML atrribute value for the tab
   */
  testId?: string;
}

export interface InputWizardProps {
  /**
   * Component reference for the InputWizard
   */
  componentRef?: (wizard: InputWizardComponent) => void;
  /**
   * Items to be displayed on the InputWizard
   */
  items: InputWizardItem[];
  /**
   * Determines how the user can navigate between the different tabs.
   */
  tabNavigation?: InputWizardTabNavigation;
  /**
   * Callback for the submit action on the InputWizard
   */
  onSubmit: (formValues: FormValues) => void;
  /**
   * Callback for the form state update on the InputWizard
   */
  onStatesUpdate?: (formStates: FormStates, fieldState: ValidationState, field: string, group?: string) => void;
  /**
   * Callback for the field value update on the InputWizard
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onValueUpdate?: (formValues: FormValues, fieldValue: any, field: string, group?: string) => void;
  /**
   * Callback to validate values on the InputWizard
   */
  validator?: (formValues: FormValues) => FormFieldErrors[] | undefined;
}

const enum NavigationActionKind {
  NEXT = "NEXT",
  PREVIOUS = "PREVIOUS",
  GOTO = "GOTO",
  LAST = "LAST",
}

type NavigationAction = {
  kind: NavigationActionKind;
  value?: number;
};

const pivotStyles: Partial<IPivotStyles> = {
  root: { marginBottom: "12px" },
  link: { height: "36px" },
};

registerIcons({ icons: { "error-svg": CircleMultiplySolid } });

/**
 * The InputWizard component renders the pivot items within a form with options
 * to navigate between each pivot item.
 */
export const InputWizard = (
  {
    componentRef,
    onSubmit,
    onStatesUpdate,
    onValueUpdate,
    items,
    tabNavigation = InputWizardTabNavigation.FREE,
    validator,
  }: InputWizardProps,
): JSX.Element => {
  function selectedKeyReducer(selectedKey: number, navigationAction: NavigationAction): number {
    const count = items.length;
    let key = selectedKey;
    const currentKey = selectedKey;
    switch (navigationAction.kind) {
      case NavigationActionKind.NEXT:
        if ((key + 1) < count) {
          key += 1;
        }
        break;
      case NavigationActionKind.PREVIOUS:
        if ((key - 1) >= 0) {
          key -= 1;
        }
        break;
      case NavigationActionKind.GOTO:
        if (navigationAction.value !== undefined) {
          if (tabNavigation === InputWizardTabNavigation.FREE
              || (tabNavigation === InputWizardTabNavigation.SEQUENTIAL && navigationAction.value < currentKey)) {
            // For SEQUENTIAL navigation block any tab selection beyond teh current one
            key = navigationAction.value;
          }
        }
        break;
      case NavigationActionKind.LAST:
        key = count - 1;
        break;
      default:
        break;
    }
    if (currentKey !== key) {
      items[currentKey].onUnselecting?.(currentKey);
    }
    return key;
  }

  const [selectedKey, dispatch] = React.useReducer(selectedKeyReducer, 0);
  const [formStates, setFormStates] = React.useState<FormStates>({} as FormStates);

  const formRef = React.useRef<FormInterface>();
  const setFormRef = (form: FormInterface): void => {
    formRef.current = form;
  };

  const enableValidation = (group?: string, noNavigation = false): void => {
    formRef.current?.enableValidation(group);
    if (!noNavigation) {
      dispatch({ kind: NavigationActionKind.LAST });
    }
  };
  const getFormFieldLabelMap = (): Map<string, string> | undefined => formRef.current?.getFormFieldLabelMap();

  const getFormFieldGroupMap = (): Map<string, string> | undefined => formRef.current?.getFormFieldGroupMap();

  const getFormValues = (): FormValues | undefined => formRef.current?.getFormValues();

  const isValidationEnabled = (group?: string): boolean | undefined => formRef.current?.isValidationEnabled(group);

  const hasValidation = (group?: string): boolean | undefined => formRef.current?.hasValidation(group);

  const submit = (): void => {
    formRef.current?.submit();
  };

  const next = (): void => {
    dispatch({ kind: NavigationActionKind.NEXT });
  };

  const previous = (): void => {
    dispatch({ kind: NavigationActionKind.PREVIOUS });
  };

  React.useEffect(() => {
    if (componentRef) {
      componentRef({
        enableValidation,
        isValidationEnabled,
        hasValidation,
        submit,
        next,
        previous,
        getFormValues,
        getFormFieldLabelMap,
        getFormFieldGroupMap,
      } as InputWizardComponent);
    }
  // Only at mount time. Others props should not have side effect beyond their initial values
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (items[selectedKey].onSelected) {
      items[selectedKey].onSelected?.(selectedKey);
    }
  }, [selectedKey, items]);

  const onFormStatesUpdate = (
    states: FormStates,
    fieldState: ValidationState,
    field: string,
    group?: string,
  ): void => {
    setFormStates(states);
    if (onStatesUpdate) {
      onStatesUpdate(states, fieldState, field, group);
    }
  };

  const itemsGroups = (): string[] => items.map(item => item.groupName);

  const pivotItems = (): JSX.Element[] => items.map((item, ndx) => {
    const customRenderer = (
      link?: IPivotItemProps,
      defaultRenderer?: (link?: IPivotItemProps) => JSX.Element | null,
    ): JSX.Element | null => {
      if (!link || !defaultRenderer) {
        return null;
      }

      const isGroupInvalid = formStates.groups?.[item.groupName] === ValidationState.INVALID;

      let style = {
        flex: "0 1 100%",
        height: "36px",
      };

      if (tabNavigation === InputWizardTabNavigation.SEQUENTIAL && ndx > selectedKey) {
        style = {
          ...style,
          ...{
            opacity: "0.7",
            color: "#808080",
            filter: "grayscale(100%)",
            cursor: "default",
          },
        };
      }

      return (
        <span style={style}>
          {defaultRenderer({
            ...link,
            itemIcon: isGroupInvalid && tabNavigation === InputWizardTabNavigation.FREE
              ? link.itemIcon
              : undefined,
          })}
          {/* {defaultRenderer({ ...link, itemIcon: undefined })}
          {isGroupInvalid && (
            <Icon
              iconName="LocationDot"
              style={{
                color: "#a4262c",
                height: "1px",
                width: "1px",
                position: "relative",
                top: "-13px",
                right: "-7px",
              }}
            />
          )} */}
        </span>
      );
    };

    const headerText = tabNavigation === InputWizardTabNavigation.SEQUENTIAL
      ? `${ndx + 1}. ${item.header}`
      : item.header;

    return (
      <PivotItem
        key={ndx.toString()}
        itemKey={ndx.toString()}
        headerText={headerText}
        headerButtonProps={
          item.testId
            ? { "data-test-id": item.testId }
            : undefined
        }
        alwaysRender
        // itemIcon="LocationDot"
        itemIcon="error-svg"
        onRenderItemLink={customRenderer}
      >
        <FormInputGroup
          groupName={item.groupName}
          layout={item.layout ? item.layout : FormInputGroupLayout.WIDE}
          style={{ paddingLeft: "8px", maxWidth: "700px" }}
        >
          {item.description && (
            <div style={{ marginBottom: "20px" }}>
              <FormattedString inputText={item.description} />
            </div>
          )}
          {item.content}
        </FormInputGroup>
      </PivotItem>
    );
  });

  const onLinkClick = (pivotItem?: PivotItem): void => dispatch({
    kind: NavigationActionKind.GOTO,
    value: Number(pivotItem?.props.itemKey),
  });

  return (
    <Form
      componentRef={setFormRef}
      groups={itemsGroups()}
      onStatesUpdate={onFormStatesUpdate}
      onValueUpdate={onValueUpdate}
      onSubmit={onSubmit}
      validator={validator}
    >
      <Pivot
        styles={pivotStyles}
        selectedKey={selectedKey.toString()}
        onLinkClick={onLinkClick}
      >
        {pivotItems()}
      </Pivot>
    </Form>
  );
};
